/* eslint-disable react-hooks/exhaustive-deps */
import React, { ChangeEvent, useEffect, useState } from 'react';
import MainLayout from '@shared/ui/main-layout';
import SelectLegalEntities from '@features/select-legal-entity';
import { SkeletonList } from '@shared/ui/skeleton-list';
import {
  fetchAllOrders,
  fetchFirstPageOrders,
  selectAllOrders,
  selectAllOrdersTypes,
  selectOrdersFetchedStatus,
  selectOrdersStatus,
} from '../../entities/order/model';
import { useMe } from '../Area/Area';
import { useAppDispatch, useAppSelector } from '../../app/store/store';
import { Tabs, Tab } from '../../shared/ui/tabs';
import { OrderList } from '@widgets/order-list';

// Карточка-обёртка таблицы — белый фон, скруглённые углы, лёгкая
// тень. Тот же приём, что и в Companies.tsx / UsersRolesSettings.tsx,
// чтобы все табличные экраны выглядели одинаково.
const TableCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
    <div className="px-6 py-4">{children}</div>
  </div>
);

function Orders() {
  const { me } = useMe();
  const dispatch = useAppDispatch();
  const orders = useAppSelector(selectAllOrders);
  const [openOrders, canceledOrders, closedOrders] = useAppSelector(selectAllOrdersTypes);
  const status = useAppSelector(selectOrdersStatus);
  const ordersFetchedStatus = useAppSelector(selectOrdersFetchedStatus);

  const [selectedLegalEntity, setSelectedLegalEntity] = useState<any>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (ordersFetchedStatus === 'succeeded') return;
    dispatch(fetchAllOrders());
  }, [dispatch, ordersFetchedStatus]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (orders.some((order) => !order.print_form)) {
        dispatch(fetchFirstPageOrders({ limit: 10 }));
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [orders, dispatch]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  function filters() {
    // ВЕРСТКА: оба элемента — фиксированная высота 48px, скругление
    // 12px, один и тот же серый border — чтобы поиск и SelectLegalEntities
    // визуально читались как одна «полоса фильтров». До правки input
    // считал высоту по py-[13px] (≈48px), а Select по своим внутренним
    // отступам (≈38px) — отсюда визуальная «лесенка». customStyles
    // в select-styles.js глобальный (его дёргает не только эта
    // страница, ещё Profile) — поэтому переопределяем точечно через
    // новый prop controlStyleOverrides, не трогая остальные места.
    return (
      <div className="w-full flex gap-[10px] items-stretch">
        <input
          className="bg-white rounded-xl max-w-[500px] w-full border border-[#0000001f] px-3 h-[48px] text-sm focus:outline-none"
          value={search}
          onChange={handleSearchChange}
          placeholder="Поиск заказов"
        />
        <SelectLegalEntities
          myLegalEntities={me.company?.legal_entities}
          selectedLegalEntity={selectedLegalEntity}
          setSelectedLegalEntity={setSelectedLegalEntity}
          controlStyleOverrides={{
            minHeight: 48,
            height: 48,
            borderRadius: 12,
            borderColor: '#0000001f',
          }}
        />
      </div>
    );
  }

  return (
    <MainLayout>
      <MainLayout.Title>История заказов</MainLayout.Title>
      <MainLayout.Main>
        {status === 'loading' ? (
          <SkeletonList status={status} />
        ) : (
          <Tabs variant="underline" contentAfterTabs={filters}>
            <Tab title="Все" badge={orders.length} badgeTone="neutral">
              <TableCard>
                <OrderList
                  orders={orders}
                  legalEntity={selectedLegalEntity}
                  search={search}
                />
              </TableCard>
            </Tab>
            <Tab
              title="В работе"
              badge={openOrders.length}
              badgeTone="neutral"
            >
              <TableCard>
                <OrderList
                  orders={openOrders}
                  legalEntity={selectedLegalEntity}
                  search={search}
                />
              </TableCard>
            </Tab>
            <Tab
              title="Выполненные"
              badge={closedOrders.length}
              badgeTone="neutral"
            >
              <TableCard>
                <OrderList
                  orders={closedOrders}
                  legalEntity={selectedLegalEntity}
                  search={search}
                />
              </TableCard>
            </Tab>
            <Tab
              title="Отмененные"
              badge={canceledOrders.length}
              badgeTone="neutral"
            >
              <TableCard>
                <OrderList
                  orders={canceledOrders}
                  legalEntity={selectedLegalEntity}
                  search={search}
                />
              </TableCard>
            </Tab>
          </Tabs>
        )}
      </MainLayout.Main>
    </MainLayout>
  );
}

export default Orders;
